/**
 * Horse age as a display string.
 *
 * `asOf` defaults to today, which is what the Age column wants. Pass a date to get
 * age at a point in the past — e.g. age at the sale date for a sale summary, where
 * "today" would keep drifting after the horse left the ranch.
 */

// Parse YYYY-MM-DD string-wise rather than through Date(string), which reads a bare
// date as UTC midnight and lands on the previous day west of Greenwich.
const parseLocalDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [year, month, day] = value.slice(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const pluralize = (count, unit) => `${count} ${unit}${count === 1 ? '' : 's'}`;

export const calculateHorseAge = (dateOfBirth, asOf = new Date()) => {
  const birthDate = parseLocalDate(dateOfBirth);
  const reference = parseLocalDate(asOf);
  if (!birthDate || !reference) return '-';

  let years = reference.getFullYear() - birthDate.getFullYear();
  let months = reference.getMonth() - birthDate.getMonth();

  // Birthday hasn't come around yet this year.
  if (months < 0 || (months === 0 && reference.getDate() < birthDate.getDate())) {
    years--;
    months += 12;
  }

  // Day of the month hasn't come around yet either.
  if (reference.getDate() < birthDate.getDate()) {
    months--;
  }

  if (years < 0) return '-';

  // Under a year, months carry the information. Past two years, months are noise.
  if (years === 0) return pluralize(months, 'month');
  if (years === 1 && months > 0) return `${pluralize(years, 'year')} ${pluralize(months, 'month')}`;
  return pluralize(years, 'year');
};

export default calculateHorseAge;
